import React from 'react';
import { Link } from 'react-router-dom';
import { trackFreeTrial } from '../utils/tracking';

const Home = () => {
  return (
    <>
      {/* Hero */}
      <section className="hero">
        <div className="paw-decoration" style={{ top: '15%', left: '5%' }}>🐾</div>
        <div className="paw-decoration" style={{ top: '25%', right: '8%', animationDelay: '1s' }}>🐾</div>
        <div className="paw-decoration" style={{ bottom: '10%', left: '12%', animationDelay: '2s' }}>🐾</div>

        <div className="container hero-content fade-up">
          <div className="section-badge blue" style={{ marginBottom: '20px' }}>
            🐕 AI-Powered Sourcing for Recruiting &amp; Sales
          </div>
          <h1 className="hero-title">
            Unleash Your <span className="text-gradient">Sourcing Potential</span>
          </h1>
          <p className="hero-subtitle">
            Meet Cooper &amp; Roxi — your AI-powered sourcing companions. Fetch verified contact info,
            source top talent, and power your sales outreach from 100M+ records across any industry.
          </p>
          <div className="hero-actions">
            <a href="https://app.knine.ai/register" className="btn btn-gradient btn-lg" target="_blank" rel="noopener noreferrer" onClick={() => trackFreeTrial('home_hero')}>
              Start Free Trial
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <Link to="/pricing" className="btn btn-outline btn-lg">
              View Pricing
            </Link>
          </div>

          <div className="hero-stats fade-up delay-3">
            <div className="hero-stat">
              <div className="hero-stat-value">100M+</div>
              <div className="hero-stat-label">Contact Records</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">10x</div>
              <div className="hero-stat-label">Faster Sourcing</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">95%</div>
              <div className="hero-stat-label">Data Accuracy</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">50%</div>
              <div className="hero-stat-label">Cost Savings</div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Pack */}
      <section style={{ background: 'var(--white)' }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <div className="section-badge purple">Meet The Pack</div>
            <h2 className="section-title">Your AI Sourcing <span className="text-gradient">Agents</span></h2>
            <p className="section-subtitle mx-auto">
              Cooper and Roxi are AI Agents that learn and adapt to your specific sourcing needs.
              They get smarter with every search — trained on your methods, your ideal contacts,
              and the companies that matter most to your business.
            </p>
          </div>

          <div className="feature-grid-2" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="card" style={{ textAlign: 'center', padding: '40px 32px' }}>
              <img
                src="/images/Cooper.png"
                alt="Cooper - BD & Sales AI Agent"
                className="agent-mascot"
                loading="lazy"
              />
              <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text-dark)' }}>
                Cooper
              </h3>
              <div className="tag tag-blue" style={{ marginBottom: '16px' }}>BD &amp; Sales AI Agent</div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '16px' }}>
                Your business development AI agent. Cooper learns your sales playbook — the types
                of companies you target, the departments and decision-makers you need to reach,
                and the contact data that closes deals.
              </p>
              <div style={{ textAlign: 'left', padding: '16px', background: 'var(--off-white)', borderRadius: 'var(--radius-md)' }}>
                <p style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Cooper learns to:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Identify your ideal target companies and industries',
                    'Find key decision-makers and department contacts',
                    'Adapt to your unique outreach and sourcing methods',
                    'Fetch verified emails, direct dials, and company data',
                  ].map((item) => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      <span style={{ color: 'var(--primary)', fontWeight: 700, flexShrink: 0 }}>&#10003;</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="card" style={{ textAlign: 'center', padding: '40px 32px' }}>
              <img
                src="/images/roxi.png"
                alt="Roxi - Recruiting AI Agent"
                className="agent-mascot"
                loading="lazy"
              />
              <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text-dark)' }}>
                Roxi
              </h3>
              <div className="tag tag-purple" style={{ marginBottom: '16px' }}>Recruiting AI Agent</div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '16px' }}>
                Your recruiting AI agent. Roxi learns your hiring patterns — the roles you fill,
                the talent profiles you look for, and the sourcing strategies that work best for
                your firm. She gets smarter with every search.
              </p>
              <div style={{ textAlign: 'left', padding: '16px', background: 'var(--off-white)', borderRadius: 'var(--radius-md)' }}>
                <p style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Roxi learns to:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Match your preferred candidate profiles and skill sets',
                    'Source from 100M+ records using your specific criteria',
                    'Adapt to your recruiting workflow and sourcing methods',
                    'Surface contacts at companies you should be talking to',
                  ].map((item) => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      <span style={{ color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>&#10003;</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* AI Agent callout */}
          <div className="card-flat" style={{
            maxWidth: '900px',
            margin: '32px auto 0',
            padding: '32px',
            textAlign: 'center',
            background: 'var(--primary-bg)',
            border: '1px solid #dbeafe'
          }}>
            <p style={{ fontSize: '1rem', color: 'var(--text)', lineHeight: 1.7, maxWidth: '700px', margin: '0 auto' }}>
              <strong style={{ color: 'var(--primary-dark)' }}>Personalized AI that gets smarter over time.</strong>{' '}
              Cooper and Roxi are trained specific to each customer — learning your sourcing methods,
              the types of contacts you seek, the companies you should be engaging, and the right
              department contacts within those organizations.
            </p>
          </div>

          {/* Search Previews */}
          <div style={{ maxWidth: '1100px', margin: '64px auto 0' }}>
            <div className="text-center" style={{ marginBottom: '32px' }}>
              <div className="section-badge green">See Them In Action</div>
              <h3 className="section-title" style={{ fontSize: '1.75rem' }}>
                AI-Powered Search, <span className="text-gradient">Built for You</span>
              </h3>
              <p className="section-subtitle mx-auto">
                Cooper and Roxi deliver intelligent, AI-optimized search results — then enrich every
                result with company intel, recommended contacts, and AI-generated profiles ready to
                push directly into your ATS or CRM.
              </p>
            </div>

            {/* Cooper: Search + Company Details */}
            <div style={{ marginBottom: '48px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <img src="/images/Cooper.png" alt="Cooper" style={{ width: '36px', height: '36px' }} />
                <h4 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-dark)' }}>Cooper — BD &amp; Sales</h4>
                <div className="tag tag-blue">Company Intelligence</div>
              </div>
              <div className="screenshot-grid-2">
                <div>
                  <div className="screenshot-frame">
                    <div className="screenshot-bar">
                      <div className="screenshot-dot red"></div>
                      <div className="screenshot-dot yellow"></div>
                      <div className="screenshot-dot green"></div>
                      <div className="screenshot-bar-title">Cooper Search</div>
                    </div>
                    <img src="/images/cooper-search.jpg" alt="Cooper Search - AI-powered company and contact search with optimized criteria" loading="lazy" />
                  </div>
                  <div className="screenshot-label">
                    <h4>AI-Optimized Search</h4>
                    <p>Target companies with AI-driven criteria tailored to your sales playbook</p>
                  </div>
                </div>
                <div>
                  <div className="screenshot-frame">
                    <div className="screenshot-bar">
                      <div className="screenshot-dot red"></div>
                      <div className="screenshot-dot yellow"></div>
                      <div className="screenshot-dot green"></div>
                      <div className="screenshot-bar-title">Company Details</div>
                    </div>
                    <img src="/images/cooper-details.jpg" alt="Cooper company details with recommended contacts and company intelligence" loading="lazy" />
                  </div>
                  <div className="screenshot-label">
                    <h4>Company Intel &amp; Recommended Contacts</h4>
                    <p>Drill into any company for full details, key contacts, and decision-makers</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Roxi: Search + Contact Details */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <img src="/images/roxi.png" alt="Roxi" style={{ width: '36px', height: '36px' }} />
                <h4 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-dark)' }}>Roxi — Recruiting</h4>
                <div className="tag tag-purple">Candidate Intelligence</div>
              </div>
              <div className="screenshot-grid-2">
                <div>
                  <div className="screenshot-frame">
                    <div className="screenshot-bar">
                      <div className="screenshot-dot red"></div>
                      <div className="screenshot-dot yellow"></div>
                      <div className="screenshot-dot green"></div>
                      <div className="screenshot-bar-title">Roxi Search</div>
                    </div>
                    <img src="/images/roxi-search.jpg" alt="Roxi Search - AI-powered candidate sourcing with intelligent filters" loading="lazy" />
                  </div>
                  <div className="screenshot-label">
                    <h4>Intelligent Candidate Sourcing</h4>
                    <p>Find top talent with AI-driven filters matched to your hiring patterns</p>
                  </div>
                </div>
                <div>
                  <div className="screenshot-frame">
                    <div className="screenshot-bar">
                      <div className="screenshot-dot red"></div>
                      <div className="screenshot-dot yellow"></div>
                      <div className="screenshot-dot green"></div>
                      <div className="screenshot-bar-title">Candidate Profile</div>
                    </div>
                    <img src="/images/roxi-details.jpg" alt="Roxi candidate profile with AI-generated resume and contact generation" loading="lazy" />
                  </div>
                  <div className="screenshot-label">
                    <h4>AI Resume &amp; Contact Generation</h4>
                    <p>Generate professional AI resumes and verified contact info — push directly to your ATS</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="bg-off-white">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <div className="section-badge blue">🦴 Platform Features</div>
            <h2 className="section-title">Everything You Need to <span className="text-gradient">Source Smarter</span></h2>
            <p className="section-subtitle mx-auto">
              From Chrome extension to full AI search — we've got every trick in the book to help you
              find and connect with the right people.
            </p>
          </div>

          <div className="feature-grid">
            <div className="card">
              <div className="card-icon blue">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text-dark)' }}>
                Chrome Extension
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                Source contact info directly from LinkedIn, job boards, and company sites without leaving your browser.
              </p>
            </div>

            <div className="card">
              <div className="card-icon purple">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text-dark)' }}>
                AI-Powered Search
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                Use natural language queries to search 100M+ records. Describe who you're looking for and let AI do the digging.
              </p>
            </div>

            <div className="card">
              <div className="card-icon green">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              </div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text-dark)' }}>
                Analytics Dashboard
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                Track your sourcing metrics, credit usage, team performance, and ROI all in one centralized dashboard.
              </p>
            </div>

            <div className="card">
              <div className="card-icon amber">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text-dark)' }}>
                ATS/CRM Integrations
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                Seamlessly connect with your existing ATS and CRM tools. Push sourced contacts directly into your workflow.
              </p>
            </div>

            <div className="card">
              <div className="card-icon blue">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text-dark)' }}>
                Team Collaboration
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                Share sourcing lists, collaborate on searches, and manage credits across your entire team.
              </p>
            </div>

            <div className="card">
              <div className="card-icon purple">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 18 22 12 16 6" />
                  <polyline points="8 6 2 12 8 18" />
                </svg>
              </div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text-dark)' }}>
                Custom Integrations
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                Need a custom connection? Our team builds tailored integrations to fit your specific workflow and tech stack.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics & Insights */}
      <section>
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <div className="section-badge purple">Analytics &amp; Insights</div>
            <h2 className="section-title">Real-Time <span className="text-gradient">Intelligence Dashboard</span></h2>
            <p className="section-subtitle mx-auto">
              Track pipeline health, team performance, AI-driven insights, and revenue forecasting
              — all in one powerful analytics suite connected to your live data.
            </p>
          </div>

          {/* Main dashboard screenshot - Pipeline & Leaderboard */}
          <div style={{ maxWidth: '1000px', margin: '0 auto 32px' }}>
            <div className="screenshot-frame">
              <div className="screenshot-bar">
                <div className="screenshot-dot red"></div>
                <div className="screenshot-dot yellow"></div>
                <div className="screenshot-dot green"></div>
                <div className="screenshot-bar-title">Pipeline &amp; Leaderboard</div>
              </div>
              <img src="/images/analytics-1.png" alt="Cooper BD Pipeline with open positions and team leaderboard" loading="lazy" />
            </div>
          </div>

          {/* Detail screenshots */}
          <div className="screenshot-grid-2" style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div>
              <div className="screenshot-frame">
                <div className="screenshot-bar">
                  <div className="screenshot-dot red"></div>
                  <div className="screenshot-dot yellow"></div>
                  <div className="screenshot-dot green"></div>
                  <div className="screenshot-bar-title">AI Insights &amp; Stats</div>
                </div>
                <img src="/images/analytics-2.png" alt="AI Insights and Recommendations with Roxi and Cooper sourcing statistics" loading="lazy" />
              </div>
              <div className="screenshot-label">
                <h4>AI Insights &amp; Recommendations</h4>
                <p>Get actionable AI-driven insights and track sourcing performance across agents</p>
              </div>
            </div>
            <div>
              <div className="screenshot-frame">
                <div className="screenshot-bar">
                  <div className="screenshot-dot red"></div>
                  <div className="screenshot-dot yellow"></div>
                  <div className="screenshot-dot green"></div>
                  <div className="screenshot-bar-title">kNine Analytics Dashboard</div>
                </div>
                <img src="/images/analytics-3.png" alt="kNine Analytics Dashboard - Company Health Score, revenue metrics, pipeline funnel, and performance tracking" loading="lazy" />
              </div>
              <div className="screenshot-label">
                <h4>Full Analytics Dashboard</h4>
                <p>Company health scores, revenue metrics, pipeline funnel, and performance tracking</p>
              </div>
            </div>
          </div>

          {/* Analytics callout */}
          <div className="card-flat" style={{
            maxWidth: '800px',
            margin: '40px auto 0',
            padding: '28px 32px',
            textAlign: 'center',
            background: 'var(--primary-bg)',
            border: '1px solid #dbeafe'
          }}>
            <p style={{ fontSize: '0.95rem', color: 'var(--text)', lineHeight: 1.7, maxWidth: '650px', margin: '0 auto' }}>
              <strong style={{ color: 'var(--primary-dark)' }}>Live data, not stale reports.</strong>{' '}
              kNine analytics connect directly to your operational database, delivering real-time visibility
              into pipeline health, team activity, and revenue forecasts that traditional platforms can't match.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section>
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <div className="section-badge green">How It Works</div>
            <h2 className="section-title">Fetch Results in <span className="text-gradient">3 Simple Steps</span></h2>
          </div>

          <div className="feature-grid" style={{ maxWidth: '900px', margin: '0 auto' }}>
            {[
              {
                step: '1',
                title: 'Search or Browse',
                desc: 'Use natural language AI search or browse LinkedIn, job boards, and more with our Chrome extension.',
                icon: '🔍'
              },
              {
                step: '2',
                title: 'Fetch Contact Info',
                desc: 'Cooper & Roxi instantly retrieve verified emails, phone numbers, and company data for your prospects.',
                icon: '🐕'
              },
              {
                step: '3',
                title: 'Export & Connect',
                desc: 'Push contacts directly to your ATS/CRM or export to CSV. Start building relationships immediately.',
                icon: '🚀'
              }
            ].map((item) => (
              <div key={item.step} className="card-flat" style={{ textAlign: 'center', padding: '40px 28px' }}>
                <div style={{
                  fontSize: '2.5rem',
                  marginBottom: '16px'
                }}>
                  {item.icon}
                </div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: 'var(--primary)',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  marginBottom: '12px'
                }}>
                  {item.step}
                </div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '10px', color: 'var(--text-dark)' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* kNine Verified — How We Validate */}
      <section id="validation">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <div className="section-badge blue">🛡️ kNine Verified™</div>
            <h2 className="section-title">How We Validate <span className="text-gradient">Every Contact</span></h2>
            <p className="section-subtitle mx-auto">
              We don't just scrape a contact and hand it to you. Every email and phone number is checked
              in real time before it's returned — so what you see is what you can actually use.
            </p>
          </div>

          {/* Email validation */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-dark)', margin: 0 }}>
                📧 Email Validation
              </h3>
              <div className="tag tag-blue">Real-Time Deliverability Check</div>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: '760px', marginBottom: '24px' }}>
              Every email runs through a real-time deliverability check — an MX &amp; SMTP handshake, disposable
              and spam-trap screening, and a free-provider flag — before it's ever surfaced as a result.
            </p>
            <div className="feature-grid">
              {[
                { icon: '✅', title: 'Valid', desc: 'Confirmed deliverable at the mailbox level — safe to send.' },
                { icon: '📬', title: 'Catch-all', desc: 'Domain accepts all mail; flagged since deliverability isn\'t guaranteed.' },
                { icon: '⚠️', title: 'Risky', desc: 'Higher bounce risk (role-based or low quality) — proceed with caution.' },
                { icon: '❌', title: 'Invalid', desc: 'Confirmed undeliverable — filtered out, never shown as a primary contact.' },
                { icon: '🗑️', title: 'Disposable', desc: 'Temporary/burner domains — flagged so they\'re never prioritized.' },
                { icon: '❔', title: 'Unknown', desc: 'Couldn\'t be confirmed (timeout/greylisting) — shown with a clear status tag.' },
              ].map((item) => (
                <div key={item.title} className="card" style={{ padding: '20px' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>{item.icon}</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '6px' }}>
                    {item.title}
                  </h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: 1.6, margin: 0 }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Phone validation */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-dark)', margin: 0 }}>
                📱 Phone Validation
              </h3>
              <div className="tag tag-purple">Powered by Twilio Lookup</div>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: '760px', marginBottom: '24px' }}>
              Every phone number is identified by line type and carrier before it's returned, so you know
              how to reach out before you dial.
            </p>
            <div className="feature-grid-4">
              {[
                { icon: '📱', title: 'Mobile', desc: 'Best for texting & direct calls — prioritized for outreach.' },
                { icon: '☎️', title: 'Landline', desc: 'Office/desk lines, confirmed before you dial.' },
                { icon: '💻', title: 'VoIP', desc: 'Internet-based lines (Google Voice, RingCentral, etc.) — flagged clearly.' },
                { icon: '❔', title: 'Unknown', desc: 'Carrier lookup inconclusive — shown transparently, never guessed.' },
              ].map((item) => (
                <div key={item.title} className="card" style={{ padding: '20px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>{item.icon}</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '6px' }}>
                    {item.title}
                  </h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: 1.6, margin: 0 }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Smart priority ordering */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '16px' }}>
              🎯 Smart Priority Ordering
            </h3>
            <div className="feature-grid-2">
              <div className="card" style={{ padding: '24px' }}>
                <div className="tag tag-purple" style={{ marginBottom: '10px' }}>Roxi — Recruiting</div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.7, margin: 0 }}>
                  Verified personal email first, then verified phone, then work email — candidates check
                  their personal inbox far more reliably than a current employer's address.
                </p>
              </div>
              <div className="card" style={{ padding: '24px' }}>
                <div className="tag tag-blue" style={{ marginBottom: '10px' }}>Cooper — BD &amp; Sales</div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.7, margin: 0 }}>
                  Verified work email first, then verified phone, then personal email — outreach to a
                  decision-maker should go through their company inbox.
                </p>
              </div>
            </div>
          </div>

          {/* Callout */}
          <div className="card-flat" style={{
            maxWidth: '800px',
            margin: '0 auto',
            padding: '24px 32px',
            textAlign: 'center',
            background: 'var(--primary-bg)',
            border: '1px solid #dbeafe'
          }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text)', lineHeight: 1.7, margin: 0 }}>
              <strong style={{ color: 'var(--primary-dark)' }}>Only verified contacts get pushed to your ATS/CRM.</strong>{' '}
              A "kNine Verified" email or phone is what becomes the primary contact field — never an
              unconfirmed guess.
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof / Trust */}
      <section className="bg-off-white">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <div className="section-badge blue">Trusted By Professionals</div>
            <h2 className="section-title">Built for <span className="text-gradient">Recruiting &amp; Sales</span></h2>
            <p className="section-subtitle mx-auto">
              Whether you're a recruiter, a sales team, or a growing firm in any industry — kNine scales with your sourcing needs.
            </p>
          </div>

          <div className="feature-grid-4">
            {[
              { value: '100M+', label: 'Contact Records', icon: '📊' },
              { value: '10x', label: 'Faster Sourcing', icon: '⚡' },
              { value: '95%', label: 'Email Accuracy', icon: '✅' },
              { value: '8+', label: 'ATS Integrations', icon: '🔗' },
            ].map((stat) => (
              <div key={stat.label} className="card-flat" style={{ textAlign: 'center', padding: '32px 20px' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>{stat.icon}</div>
                <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '4px' }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="container">
          <div className="cta-section">
            <div className="cta-paw" style={{ top: '-20px', left: '-10px', transform: 'rotate(-25deg)' }}>🐾</div>
            <div className="cta-paw" style={{ bottom: '-20px', right: '-10px', transform: 'rotate(15deg)' }}>🐾</div>
            <h2>Ready to Unleash Your Sourcing?</h2>
            <p>
              Join recruiting and sales teams who source 10x faster with kNine. Start your free trial today — no credit card required.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://app.knine.ai/register" className="btn btn-white btn-lg" style={{ border: 'none' }} target="_blank" rel="noopener noreferrer" onClick={() => trackFreeTrial('home_cta')}>
                Start Free Trial
              </a>
              <Link to="/pricing" className="btn btn-lg" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
